import type { ActivityOf } from '@wyn/content';
import { assertGame } from '../errors.ts';
import type { Rng } from '../rng.ts';
import type { MinigameResult, TurnEngineImpl } from './types.ts';

export const DICE_GOAL = 40;
/** The house needs less: rolling first is worth about six points of win rate, and the house must keep an edge. */
export const DICE_HOUSE_GOAL = 31;
/** One palm per 4 Gambling levels, but no more than this in a game: the croupier only looks away so often. */
export const DICE_MAX_PALMS = 3;

/**
 * The chance the house spots a palmed die (and the game is forfeit), falling with Gambling level to a
 * floor at level 12. Palming a big pot breaks even at about 0.47, so it's a trap below level 10 and
 * pays a little from 12; the house keeps its edge however well you palm.
 */
export const palmRisk = (level: number) => Math.max(0.45, 0.6 - level * 0.0125);
/** After a win you may let it ride this many times: a fresh game, double or nothing. */
export const DICE_MAX_RIDES = 1;
/** Winning after trailing the house by this much is a comeback. */
export const DICE_COMEBACK = 15;

type Pair = [number, number];

export interface DiceState {
  me: number;
  house: number;
  pot: number;
  palms: number;
  /** The chance a palm is spotted, fixed when you sit down. */
  palmRisk: number;
  /** What the player may do next: roll or bank, (after a one) palm the die or pass, or (after a win) collect or ride. */
  awaiting: 'roll' | 'palm' | 'ride' | 'over';
  /** Times the winnings were let ride. Older sessions have no field. */
  rides?: number;
  last: Pair | null;
  /** The pot before the roll a palm would undo. */
  savedPot: number;
  /** The house's rolls on its most recent turn, for the table to replay. */
  houseRolls: Pair[];
  say: string;
  won: boolean | null;
  /** The most the player has trailed the house by. Older sessions have no field. */
  behind?: number;
  /** The house has rolled snake eyes this game. Older sessions have no field. */
  houseSnake?: boolean;
  /** The croupier caught a palmed die. Older sessions have no field. */
  caught?: boolean;
}

export type DiceMove = { type: 'roll' } | { type: 'bank' } | { type: 'palm' } | { type: 'pass' } | { type: 'ride' } | { type: 'collect' };

export type DiceView = Omit<DiceState, 'savedPot' | 'behind' | 'houseSnake' | 'caught' | 'rides'> & {
  goal: number;
  houseGoal: number;
  rides: number;
  /** What a win pays now. */
  winnings: number;
};

/** What winning pays: twice the stake, doubled again for each ride. */
const winnings = (act: ActivityOf<'dice'>, s: DiceState) => act.stake * 2 ** (1 + (s.rides ?? 0));

const d6 = (rng: Rng) => rng.int(1, 6);

/** Snake eyes wipes the roller's score and the pot; a single one loses the pot. */
function resolve(s: DiceState, who: 'me' | 'house', [a, b]: Pair): 'snake' | 'bust' | 'ok' {
  if (a === 1 && b === 1) {
    s[who] = 0;
    s.pot = 0;
    return 'snake';
  }
  if (a === 1 || b === 1) {
    s.pot = 0;
    return 'bust';
  }
  s.pot += a + b;
  return 'ok';
}

function houseTurn(act: ActivityOf<'dice'>, s: DiceState, rng: Rng, lead: string) {
  s.houseRolls = [];
  let said = '';
  for (;;) {
    const roll: Pair = [d6(rng), d6(rng)];
    s.houseRolls.push(roll);
    const r = resolve(s, 'house', roll);
    if (r !== 'ok') {
      said = r === 'snake' ? 'Snake eyes! The house loses everything.' : 'A one! The house busts.';
      if (r === 'snake') s.houseSnake = true;
      break;
    }
    const want = act.smart ? (s.me >= DICE_GOAL - 12 ? 26 : s.house < s.me ? 22 : 18) : act.houseBanks;
    if (s.house + s.pot >= DICE_HOUSE_GOAL || s.pot >= want) {
      said = `The house banks ${s.pot}.`;
      s.house += s.pot;
      s.pot = 0;
      break;
    }
  }
  s.say = `${lead} ${said}`.trim();
  s.behind = Math.max(s.behind ?? 0, s.house - s.me);
  if (s.house >= DICE_HOUSE_GOAL) {
    s.awaiting = 'over';
    s.won = false;
  } else s.awaiting = 'roll';
}

function afterMine(act: ActivityOf<'dice'>, s: DiceState, r: ReturnType<typeof resolve>, rng: Rng) {
  const [a, b] = s.last!;
  if (r === 'ok') {
    s.say = `+${a + b}. Roll again or bank?`;
    s.awaiting = 'roll';
  } else if (r === 'bust' && s.palms > 0) {
    s.say = 'A one! Palm it and reroll, or pass?';
    s.awaiting = 'palm';
  } else houseTurn(act, s, rng, r === 'snake' ? 'Snake eyes! You lose everything.' : 'A one! Your pot is lost.');
}

/**
 * The Dice Cellar: race to 40 against the house. Roll to build a pot and bank it before a one shows.
 * Gambling 4 and up lets you palm a die to reroll a one. After a win you may collect, or let it ride:
 * a fresh game for double or nothing, with whatever palms are left. Ported from the Harrowmere
 * prototype. Every roll comes from the session's provably fair RNG (server seed + client seed).
 */
export const dice: TurnEngineImpl<ActivityOf<'dice'>, DiceState, DiceMove, DiceView> = {
  init(_a, level) {
    return { me: 0, house: 0, pot: 0, palms: Math.min(DICE_MAX_PALMS, Math.floor(level / 4)), palmRisk: palmRisk(level), awaiting: 'roll', last: null, savedPot: 0, houseRolls: [], say: 'Your roll.', won: null };
  },

  move(act, prev, m, { rng }) {
    const s: DiceState = { ...prev, houseRolls: [] };
    assertGame(s.awaiting !== 'over', 'INVALID', 'The game is over.');
    switch (m.type) {
      case 'roll': {
        assertGame(s.awaiting === 'roll', 'INVALID', s.awaiting === 'ride' ? 'Collect or let it ride first.' : 'Palm the die or pass first.');
        s.savedPot = s.pot;
        s.last = [d6(rng), d6(rng)];
        afterMine(act, s, resolve(s, 'me', s.last), rng);
        break;
      }
      case 'bank': {
        assertGame(s.awaiting === 'roll' && s.pot > 0, 'INVALID', 'There is nothing to bank.');
        const banked = s.pot;
        s.me += s.pot;
        s.pot = 0;
        if (s.me >= DICE_GOAL) {
          s.won = true;
          const more = (s.rides ?? 0) < DICE_MAX_RIDES;
          s.awaiting = more ? 'ride' : 'over';
          s.say = `You bank ${banked} and take the pot!${more ? ` Collect ${winnings(act, s)}, or let it ride for ${winnings(act, s) * 2}?` : ''}`;
        } else houseTurn(act, s, rng, `You bank ${banked}.`);
        break;
      }
      case 'collect': {
        assertGame(s.awaiting === 'ride', 'INVALID', 'There is nothing to collect yet.');
        s.awaiting = 'over';
        s.say = `You collect ${winnings(act, s)}.`;
        break;
      }
      case 'ride': {
        assertGame(s.awaiting === 'ride' && (s.rides ?? 0) < DICE_MAX_RIDES, 'INVALID', 'There is nothing to let ride.');
        Object.assign(s, { me: 0, house: 0, pot: 0, last: null, won: null, awaiting: 'roll', behind: 0, rides: (s.rides ?? 0) + 1 });
        s.say = `You let it ride: ${winnings(act, s)} or nothing. Your roll.`;
        break;
      }
      case 'palm': {
        assertGame(s.awaiting === 'palm' && s.palms > 0, 'INVALID', 'There is no die to palm.');
        s.palms--;
        if (rng.next() < s.palmRisk) {
          s.awaiting = 'over';
          s.won = false;
          s.caught = true;
          s.say = 'The croupier grabs your wrist. Palmed! The pot is the house’s.';
          break;
        }
        const last: Pair = [...s.last!];
        last[last[0] === 1 ? 0 : 1] = d6(rng);
        s.last = last;
        s.pot = s.savedPot;
        afterMine(act, s, resolve(s, 'me', last), rng);
        if ((s.awaiting as DiceState['awaiting']) === 'roll') s.say = `A deft palm… ${s.say}`;
        break;
      }
      case 'pass': {
        assertGame(s.awaiting === 'palm', 'INVALID', 'Nothing to pass on.');
        houseTurn(act, s, rng, 'You curse and pass.');
        break;
      }
    }
    return s;
  },

  view(act, s) {
    const { savedPot: _saved, behind: _behind, houseSnake: _snake, caught: _caught, rides, ...rest } = s;
    return { ...rest, goal: DICE_GOAL, houseGoal: DICE_HOUSE_GOAL, rides: rides ?? 0, winnings: winnings(act, s) };
  },

  result(act, s) {
    if (s.awaiting !== 'over') return null;
    const win = s.won === true;
    const rode = (s.rides ?? 0) > 0;
    const coin = win ? winnings(act, s) : 0;
    return {
      // A ride is a wager on coin alone: the game that was won still counts as won.
      score: win || rode ? 1 : 0.3,
      title: win ? `You win ${coin} coin` : rode ? `You let it ride and lose ${act.stake * 2} coin` : `You lose your ${act.stake} coin`,
      text: `Final score ${s.me}–${s.house}.`,
      breakdown: [['Your score', Math.min(1, s.me / DICE_GOAL)]],
      yield: null,
      coin,
      xpMult: 1,
      feats: [
        ...(win && rode ? ['rode-and-won'] : []),
        ...(win && (s.behind ?? 0) >= DICE_COMEBACK ? ['comeback'] : []),
        ...(s.houseSnake ? ['house-snake-eyes'] : []),
        ...(s.caught ? ['caught-palming'] : []),
      ],
    };
  },
};
